// Inventory data layer (Phase 4) — the live stock ledger + aggregate.
//
// The SINGLE interface between the app and the inventory schema
// (supabase/migrations/20260630194946_inventory.sql).
//
// MODEL:
//   • stock_movements = append-only ledger (every change is a signed-quantity row).
//   • stock_items     = one aggregate per product (current/reserved/available, avg
//     cost, last purchase cost, reorder level, location). current_stock & average_cost
//     are DERIVED from the ledger by recomputeProduct(); reorder_level & location are
//     user-owned and preserved across recomputes. available_stock & stock_status are
//     DB-generated columns.
//
// P4-CC15b-2a/2b — every write to stock_movements/stock_items (replace, delete-only
// rollback, and the aggregate recompute) now goes through three security-definer RPCs
// — stock_replace_movements()/stock_remove_movements()/stock_recompute()
// (supabase/migrations/20260803150000_inventory_definer.sql) — NOT raw
// .from(MOVES)/.from(ITEMS) writes. stock_remove_movements() exists specifically for
// removeGRN()/removeSalesOrder()/removeDeliveryNote(), which run AFTER their parent
// document row is already deleted — stock_replace_movements()'s own source-ownership
// check would find nothing and raise on every legitimate cleanup, which is why a
// separate delete-only function authorises on can_write(<resource>) alone instead.
// createStockItem()/updateStockItem() are the one exception: they write user-owned
// fields (reorder level, location), not ledger-derived ones, and stay direct writes.
//
// GRN INTEGRATION (idempotent, mirrors the PO-sync pattern):
//   • syncGRN(grnId)   — replace this GRN's receipt movements, then recompute.
//   • removeGRN(grnId) — drop this GRN's movements, then recompute (rollback).
//   Called by src/lib/goodsReceived.js after every GRN create/update/delete.
//
// RETURN-TYPE CONTRACT: collection → { data, count, page?, pageSize? } ·
//   single → T|null · create/update → id · adjust/sync → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { C } from "../customers/theme";

const ITEMS = "stock_items";
const MOVES = "stock_movements";

export const DEFAULT_PAGE_SIZE = 25;

export const STOCK_STATUSES = [
  { value: "ok", label: "In Stock", color: C.success },
  { value: "low", label: "Low Stock", color: C.warning },
  { value: "out", label: "Out of Stock", color: C.danger },
];
export const STOCK_SORTS = [
  { value: "name", label: "Product" },
  { value: "sku", label: "SKU" },
  { value: "current", label: "Current Stock" },
  { value: "available", label: "Available" },
  { value: "status", label: "Status" },
];
export const MOVEMENT_TYPES = {
  receipt: { label: "Goods Receipt", color: C.success },
  adjustment: { label: "Adjustment", color: C.primary },
  sale: { label: "Sale", color: C.purple },
  reservation: { label: "Reservation", color: C.warning },
  release: { label: "Release", color: C.textMuted },
  return: { label: "Return", color: C.success },
  write_off: { label: "Write-off", color: C.danger },
};

const ITEM_USER_FIELDS = ["reorder_level", "warehouse_location"];
const SORT_COLUMNS = { name: "name", sku: "sku", current: "current_stock", available: "available_stock", status: "stock_status", updated: "updated_at" };

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const num = (n) => Number(n) || 0;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "That product is already tracked in inventory."; break;
    case "23503": message = "Referenced product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "23514": message = "Invalid value for an inventory field."; break;
    case "PGRST116": message = "Stock item not found, or you do not have access to it."; break;
    // P4-CC15b-2b backstop: createStockItem()/updateStockItem() became owner/admin-only
    // when stock_items/stock_movements moved to RBAC. The UI hides/disables the buttons
    // that reach these (canWriteInventory(), below) so this should be unreachable in
    // normal use — but permissions can change mid-session (a role_permissions override),
    // so a caller who gets here anyway must see an honest message, never the raw
    // Postgres RLS string.
    case "42501": message = "You do not have permission to change stock levels."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List stock items (RLS-scoped) with search, status filter, sort, pagination.
 * @param {Object} [opts] search, status('all'|'ok'|'low'|'out'), sortBy, sortDir, page, pageSize
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getStockItems(opts = {}) {
  const { search, status, sortBy = "name", sortDir = "asc", page = 1, pageSize = DEFAULT_PAGE_SIZE } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "name";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(ITEMS).select("*", { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("stock_status", status);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`sku.ilike.%${term}%,name.ilike.%${term}%,warehouse_location.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load inventory");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchStockItems(term, opts = {}) { return getStockItems({ ...opts, search: term }); }

export async function getStockItem(id) {
  const { data, error } = await client().from(ITEMS).select("*").eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load stock item");
  return data || null;
}

/** Products not yet tracked in inventory — for the "Add Stock Item" picker. */
export async function getUntrackedProducts() {
  const c = client();
  const { data: tracked } = await c.from(ITEMS).select("product_id");
  const ids = new Set((tracked || []).map((t) => t.product_id));
  const { data: products, error } = await c.from("products").select("id, name, sku, cost_price").order("name", { ascending: true });
  if (error) throw toError(error, "Could not load products");
  return { data: (products || []).filter((p) => !ids.has(p.id)) };
}

/**
 * Movement ledger for a product (newest first), with pagination.
 * @returns {Promise<{data:Array,count:number}>}
 */
export async function getStockMovements(opts = {}) {
  const { productId, page = 1, pageSize = 50 } = opts;
  const c = client();
  const safePage = Math.max(1, Number(page) || 1);
  const from = (safePage - 1) * pageSize;
  let q = c.from(MOVES).select("*", { count: "exact" }).order("created_at", { ascending: false }).range(from, from + pageSize - 1);
  if (productId) q = q.eq("product_id", productId);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load stock movements");
  return { data: data || [], count: count ?? 0 };
}

// ── Internals: aggregate recomputation ───────────────────────────────────────

async function fetchProductMeta(ids) {
  if (!ids.length) return {};
  const { data } = await client().from("products").select("id, name, sku").in("id", ids);
  const map = {};
  (data || []).forEach((p) => { map[p.id] = p; });
  return map;
}

/**
 * Recompute a product's aggregate (current stock, avg cost, last purchase) from its
 * ledger. CC15b-2a: delegates to the stock_recompute() security-definer RPC, which
 * reproduces this exact arithmetic server-side (see 20260803150000_inventory_definer.sql)
 * — this wrapper exists only so every call site below is unchanged.
 */
async function recomputeProduct(product_id) {
  const { error } = await client().rpc("stock_recompute", { p_product_ids: [product_id] });
  if (error) throw toError(error, "Could not update stock item");
}

// ── Permission check (P4-CC15b-2b) ───────────────────────────────────────────

/**
 * Whether the current user may write stock_items/stock_movements DIRECTLY —
 * owner/admin by default; CC15c may grant others via a role_permissions
 * override. The UI uses this to hide/disable createStockItem()/
 * updateStockItem()'s controls, rather than show a button to a role the
 * database will refuse, which just looks like the app is broken (a raw RLS
 * message, or "not found" about an item plainly on screen).
 *
 * THROWS rather than swallows an RPC failure — the caller must treat that as
 * NOT PERMITTED (fail closed). A false "you may not" is a support question;
 * a false "you may" is a button that always fails.
 */
export async function canWriteInventory() {
  const { data, error } = await client().rpc("can_write", { p_resource: "inventory" });
  if (error) throw toError(error, "Could not check inventory permissions");
  return data === true;
}

// ── Writes (user-facing) ─────────────────────────────────────────────────────

/** Start tracking a product in inventory (current stock 0; optional reorder/location). */
export async function createStockItem(payload) {
  const c = client();
  const meta = (await fetchProductMeta([payload.product_id]))[payload.product_id];
  const row = { product_id: payload.product_id, sku: meta?.sku || payload.sku || null, name: meta?.name || payload.name || null, ...pick(payload, ITEM_USER_FIELDS) };
  const { data, error } = await c.from(ITEMS).insert(row).select("id").single();
  if (error) throw toError(error, "Could not add stock item");
  return data.id;
}

/** Update user-owned fields only (reorder level, warehouse location). */
export async function updateStockItem(id, payload) {
  const { data, error } = await client().from(ITEMS).update(pick(payload, ITEM_USER_FIELDS)).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update stock item");
  return data.id;
}

/**
 * Manual stock adjustment — writes a signed 'adjustment' movement and recomputes.
 * CC15b-2a: routes through stock_replace_movements(source_type='manual') so every
 * stock write goes through the same door; 'manual' has no source document, so the
 * RPC's delete step is a no-op here (each adjustment is its own event, never replaced).
 * @param {Object} p { product_id, quantity (signed delta), reason?, unit_cost? }
 */
export async function adjustStock(p) {
  const c = client();
  const meta = (await fetchProductMeta([p.product_id]))[p.product_id];
  const { error } = await c.rpc("stock_replace_movements", {
    p_source_type: "manual",
    p_source_id: null,
    p_rows: [{
      product_id: p.product_id, sku: meta?.sku || null, name: meta?.name || null,
      movement_type: "adjustment", quantity: round3(p.quantity), unit_cost: p.unit_cost ?? null,
      reference: "Manual adjustment", notes: p.reason || null,
    }],
  });
  if (error) throw toError(error, "Could not adjust stock");
  await recomputeProduct(p.product_id);
}

// ── Writes (GRN integration) ─────────────────────────────────────────────────

/**
 * Reconcile inventory to a GRN's current state. Replaces this GRN's receipt movements
 * (idempotent) and recomputes every affected product. Non-product (freeform) lines and
 * cancelled/rejected GRNs contribute no stock.
 * @param {string} grnId
 */
export async function syncGRN(grnId) {
  if (!grnId) return;
  const c = client();
  const { data: grn } = await c.from("goods_received_notes")
    .select("grn_number, status, goods_received_note_lines ( id, product_id, sku, quantity_received, unit_cost, batch_number, expiry_date )")
    .eq("id", grnId).maybeSingle();
  if (!grn) { await removeGRN(grnId); return; }

  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "grn").eq("source_id", grnId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));

  const active = grn.status !== "cancelled" && grn.status !== "rejected";
  const lines = active ? (grn.goods_received_note_lines || []).filter((l) => l.product_id && num(l.quantity_received) > 0) : [];
  const meta = await fetchProductMeta([...new Set(lines.map((l) => l.product_id))]);

  // item_sku/name: what a NEW stock_items row gets if this product isn't tracked yet —
  // the product's own current name/sku when known, falling back to this GRN line's
  // sku snapshot only if the product lookup somehow came back empty. Deliberately
  // separate from the movement's own `sku` (below), which prefers the line's sku
  // first — this asymmetry is inherited byte-for-byte from the old ensureStockItem()
  // call (`meta[l.product_id] || { sku: l.sku }`) plus the movement insert's own
  // `l.sku || meta[l.product_id]?.sku || null`, which read differently on purpose.
  const rows = lines.map((l) => ({
    product_id: l.product_id, sku: l.sku || meta[l.product_id]?.sku || null,
    item_sku: meta[l.product_id] ? (meta[l.product_id].sku || null) : (l.sku || null),
    name: meta[l.product_id]?.name || null,
    movement_type: "receipt", quantity: round3(l.quantity_received), unit_cost: l.unit_cost ?? null,
    batch_number: l.batch_number || null, expiry_date: l.expiry_date || null,
    source_line_id: l.id, reference: grn.grn_number,
  }));
  const { error } = await c.rpc("stock_replace_movements", { p_source_type: "grn", p_source_id: grnId, p_rows: rows });
  if (error) throw toError(error, "Could not post stock receipt");
  lines.forEach((l) => affected.add(l.product_id));

  for (const pid of affected) await recomputeProduct(pid);
}

/**
 * Roll a GRN's stock back out (on GRN delete): remove its movements + recompute.
 * CC15b-2b: runs AFTER goods_received.js has already deleted the GRN row itself, so
 * stock_replace_movements() cannot be reused (its own source-ownership check would
 * find nothing and raise) — routes through the delete-only stock_remove_movements()
 * instead, authorised on can_write('goods_received') the same as the GRN delete itself.
 */
export async function removeGRN(grnId) {
  if (!grnId) return;
  const c = client();
  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "grn").eq("source_id", grnId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));
  const { error } = await c.rpc("stock_remove_movements", { p_source_type: "grn", p_source_id: grnId });
  if (error) throw toError(error, "Could not remove stock receipt");
  for (const pid of affected) await recomputeProduct(pid);
}

// ── Writes (Sales-Order reservations + Delivery-Note dispatch) ───────────────

// Delivery-note statuses that have physically moved stock (post sale/return movements).
const DISPATCH_STATUSES = ["dispatched", "delivered", "returned"];

/**
 * Reconcile a Sales Order to reality. Idempotent: replaces this order's 'reservation'
 * movements and recomputes affected products. Reserves the OUTSTANDING quantity per
 * line — min(ordered − dispatched, available) — so dispatching (via delivery notes)
 * naturally shrinks the reservation, and reserved_stock can never exceed current_stock.
 * Writes reserved_quantity + allocated_quantity(=dispatched) back to each line, and
 * advances the fulfilment status (confirmed → partially_fulfilled → fulfilled) from
 * net dispatched. Cancelled orders reserve nothing and keep their status.
 * @param {string} soId
 */
export async function syncSalesOrder(soId) {
  if (!soId) return;
  const c = client();
  const { data: so } = await c.from("sales_orders")
    .select("so_number, status, sales_order_lines ( id, product_id, sku, quantity )")
    .eq("id", soId).maybeSingle();
  if (!so) { await removeSalesOrder(soId); return; }
  const cancelled = so.status === "cancelled";
  const soLines = so.sales_order_lines || [];

  // net dispatched per SO line, from delivery notes that have physically moved
  const dispatchedByLine = {};
  const { data: dns } = await c.from("delivery_notes")
    .select("status, delivery_note_lines ( sales_order_line_id, dispatched_quantity, returned_quantity )")
    .eq("sales_order_id", soId).in("status", DISPATCH_STATUSES);
  (dns || []).forEach((dn) => (dn.delivery_note_lines || []).forEach((dl) => {
    if (dl.sales_order_line_id) dispatchedByLine[dl.sales_order_line_id] = round3((dispatchedByLine[dl.sales_order_line_id] || 0) + num(dl.dispatched_quantity) - num(dl.returned_quantity));
  }));

  // drop this order's reservations, then recompute so reserved_stock reflects OTHER
  // orders — MUST happen (and be recomputed) before the availability read below;
  // reordering this would double-count this order's own stale reservation. p_rows: []
  // makes this call delete-only; stock_replace_movements() authorises it on
  // 'sales_orders', which the caller has already had to pass to reach this function.
  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "sales_order").eq("source_id", soId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));
  {
    const { error } = await c.rpc("stock_replace_movements", { p_source_type: "sales_order", p_source_id: soId, p_rows: [] });
    if (error) throw toError(error, "Could not release stock reservations");
  }
  for (const pid of affected) await recomputeProduct(pid);

  const lines = cancelled ? [] : soLines.filter((l) => l.product_id);
  const productIds = [...new Set(lines.map((l) => l.product_id))];
  const avail = {};
  if (productIds.length) {
    const { data: items } = await c.from(ITEMS).select("product_id, current_stock, reserved_stock").in("product_id", productIds);
    (items || []).forEach((it) => { avail[it.product_id] = round3(num(it.current_stock) - num(it.reserved_stock)); });
  }

  const reservations = [];
  for (const l of lines) {
    const dispatched = Math.max(0, dispatchedByLine[l.id] || 0);
    const outstanding = Math.max(0, round3(num(l.quantity) - dispatched));   // undispatched portion
    const a = avail[l.product_id] ?? 0;
    const reserve = Math.max(0, Math.min(outstanding, a));
    avail[l.product_id] = round3(a - reserve);
    reservations.push({ lineId: l.id, product_id: l.product_id, sku: l.sku, reserve });
    affected.add(l.product_id);
  }

  // Batched into ONE stock_replace_movements call — its own delete-then-insert is a
  // no-op here (already cleared above) followed by inserting the whole new set
  // atomically, matching "replace" semantics rather than one insert per line.
  const reservationRows = reservations.filter((r) => r.reserve > 0).map((r) => ({
    product_id: r.product_id, sku: r.sku || null,
    movement_type: "reservation", quantity: round3(r.reserve),
    source_line_id: r.lineId, reference: so.so_number,
  }));
  if (reservationRows.length) {
    const { error } = await c.rpc("stock_replace_movements", { p_source_type: "sales_order", p_source_id: soId, p_rows: reservationRows });
    if (error) throw toError(error, "Could not reserve stock");
  }

  // write reserved + dispatched(allocated) back to every line
  for (const l of soLines) {
    const dispatched = round3(Math.max(0, dispatchedByLine[l.id] || 0));
    const r = reservations.find((x) => x.lineId === l.id);
    await c.from("sales_order_lines").update({ reserved_quantity: round3(r ? r.reserve : 0), allocated_quantity: dispatched }).eq("id", l.id);
  }
  for (const pid of affected) await recomputeProduct(pid);

  // automatic fulfilment status (reflects dispatch reality; never overrides 'cancelled')
  if (!cancelled) {
    const totalOrdered = soLines.reduce((s, l) => s + num(l.quantity), 0);
    const totalDispatched = soLines.reduce((s, l) => s + Math.max(0, dispatchedByLine[l.id] || 0), 0);
    let next = so.status;
    if (totalDispatched <= 0) next = ["partially_fulfilled", "fulfilled"].includes(so.status) ? "confirmed" : so.status;
    else if (totalOrdered > 0 && totalDispatched >= totalOrdered) next = "fulfilled";
    else next = "partially_fulfilled";
    if (next !== so.status) await c.from("sales_orders").update({ status: next }).eq("id", soId);
  }
}

/**
 * Release a Sales Order's reservations (on delete): remove its movements + recompute.
 * CC15b-2b: runs AFTER salesOrders.js has already deleted the sales_order row itself,
 * so stock_replace_movements() cannot be reused here — routes through the delete-only
 * stock_remove_movements() instead, authorised on can_write('sales_orders') the same
 * as the sales-order delete itself. Closes the exact gap this package exists to stop:
 * without this, a role that may delete a sales order but not write inventory directly
 * (sales, employee) would have this raw delete match zero rows once stock_movements
 * moves to RBAC, leaving the order's reservation stuck forever against a deleted order.
 */
export async function removeSalesOrder(soId) {
  if (!soId) return;
  const c = client();
  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "sales_order").eq("source_id", soId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));
  const { error } = await c.rpc("stock_remove_movements", { p_source_type: "sales_order", p_source_id: soId });
  if (error) throw toError(error, "Could not release stock reservations");
  for (const pid of affected) await recomputeProduct(pid);
}

/**
 * Reconcile inventory to a Delivery Note. Idempotent: replaces this note's 'sale' and
 * 'return' movements, recomputes affected products, then re-syncs the linked Sales
 * Order (whose reservation shrinks to the new outstanding and whose fulfilment status
 * advances). Dispatch reduces current_stock; returns add it back. Only notes in a
 * dispatched/delivered/returned status move stock — draft/picking/packed/cancelled don't.
 * @param {string} dnId
 */
export async function syncDeliveryNote(dnId) {
  if (!dnId) return;
  const c = client();
  const { data: dn } = await c.from("delivery_notes")
    .select("dn_number, status, sales_order_id, delivery_note_lines ( id, product_id, sku, dispatched_quantity, returned_quantity )")
    .eq("id", dnId).maybeSingle();
  if (!dn) { await removeDeliveryNote(dnId); return; }

  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "delivery_note").eq("source_id", dnId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));

  const moving = DISPATCH_STATUSES.includes(dn.status);
  const lines = moving ? (dn.delivery_note_lines || []).filter((l) => l.product_id) : [];
  const meta = await fetchProductMeta([...new Set(lines.map((l) => l.product_id))]);
  const rows = [];
  for (const l of lines) {
    // item_sku/name: see the identical comment in syncGRN — a NEW stock_items row
    // prefers the product's own current name/sku; the movement's own `sku` (below)
    // prefers the line's sku, with no fallback — inherited byte-for-byte.
    const itemSku = meta[l.product_id] ? (meta[l.product_id].sku || null) : (l.sku || null);
    const itemName = meta[l.product_id]?.name || null;
    if (num(l.dispatched_quantity) > 0) {
      rows.push({
        product_id: l.product_id, sku: l.sku || null, item_sku: itemSku, name: itemName,
        movement_type: "sale", quantity: -round3(l.dispatched_quantity),   // dispatch reduces current_stock
        source_line_id: l.id, reference: dn.dn_number,
      });
    }
    if (num(l.returned_quantity) > 0) {
      rows.push({
        product_id: l.product_id, sku: l.sku || null, item_sku: itemSku, name: itemName,
        movement_type: "return", quantity: round3(l.returned_quantity),    // returns add stock back
        source_line_id: l.id, reference: dn.dn_number,
      });
    }
    affected.add(l.product_id);
  }
  const { error } = await c.rpc("stock_replace_movements", { p_source_type: "delivery_note", p_source_id: dnId, p_rows: rows });
  if (error) throw toError(error, "Could not post dispatch/return");
  for (const pid of affected) await recomputeProduct(pid);
  if (dn.sales_order_id) await syncSalesOrder(dn.sales_order_id); // release reservation + update fulfilment
}

/**
 * Roll a Delivery Note's stock back (on delete): remove its movements, recompute,
 * re-sync the SO. CC15b-2b: runs AFTER deliveryNotes.js has already deleted the
 * delivery_note row itself, so stock_replace_movements() cannot be reused here —
 * routes through the delete-only stock_remove_movements() instead, authorised on
 * can_write('delivery_notes') the same as the delivery-note delete itself.
 */
export async function removeDeliveryNote(dnId, soId) {
  if (!dnId) return;
  const c = client();
  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "delivery_note").eq("source_id", dnId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));
  const { error } = await c.rpc("stock_remove_movements", { p_source_type: "delivery_note", p_source_id: dnId });
  if (error) throw toError(error, "Could not remove dispatch/return");
  for (const pid of affected) await recomputeProduct(pid);
  if (soId) await syncSalesOrder(soId);
}

/** Stock snapshot keyed by product_id (current/reserved/available) — for the SO form. */
export async function getStockMap() {
  const { data, error } = await client().from(ITEMS).select("product_id, current_stock, reserved_stock, available_stock, stock_status, reorder_level");
  if (error) throw toError(error, "Could not load stock levels");
  const map = {};
  (data || []).forEach((it) => { map[it.product_id] = it; });
  return map;
}

// ── Writes (Credit-Note restock — P4-CC7) ────────────────────────────────────

/**
 * Reconcile inventory to a Credit Note's restock lines. Idempotent (mirrors syncGRN /
 * syncDeliveryNote): replaces this credit note's 'return' movements and recomputes each
 * affected product. Only an ISSUED credit note restocks, and only its product lines
 * flagged restock (qty > 0) — service lines and drafts contribute nothing. The credit
 * note calls this on issue; inventory remains the sole owner of stock movements.
 * @param {string} cnId
 */
export async function syncCreditNote(cnId) {
  if (!cnId) return;
  const c = client();
  const { data: cn } = await c.from("credit_notes")
    .select("credit_note_number, status, credit_note_items ( id, product_id, sku, quantity, restock )")
    .eq("id", cnId).maybeSingle();

  const { data: prior } = await c.from(MOVES).select("product_id").eq("source_type", "credit_note").eq("source_id", cnId);
  const affected = new Set((prior || []).map((m) => m.product_id).filter(Boolean));

  const lines = (cn && cn.status === "issued")
    ? (cn.credit_note_items || []).filter((l) => l.restock && l.product_id && num(l.quantity) > 0)
    : [];
  const meta = await fetchProductMeta([...new Set(lines.map((l) => l.product_id))]);
  const rows = lines.map((l) => ({
    product_id: l.product_id, sku: l.sku || meta[l.product_id]?.sku || null,
    item_sku: meta[l.product_id] ? (meta[l.product_id].sku || null) : (l.sku || null),
    name: meta[l.product_id]?.name || null,
    movement_type: "return", quantity: round3(l.quantity),   // credited qty returns to stock
    source_line_id: l.id, reference: cn.credit_note_number,
  }));
  const { error } = await c.rpc("stock_replace_movements", { p_source_type: "credit_note", p_source_id: cnId, p_rows: rows });
  if (error) throw toError(error, "Could not post credit-note return");
  lines.forEach((l) => affected.add(l.product_id));
  for (const pid of affected) await recomputeProduct(pid);
}
